import { useLayoutEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"

import { cn } from "@productlane/lib/src/browser"
import {
	hasLinearMentionLookup,
	linearMentionLookup,
} from "@productlane/lib/src/linear-mentions"
import { typographyVariants } from "@productlane/ui"

import type { LinearMentionSlot } from "./linear-mention-slots"
import { api } from "@/lib/api"
import { sanitizeMessageHtml } from "@/lib/sanitize-message-html"
import { LinearMentionBadge } from "./linear-mention-badge"
import { claimLinearMentionSlots } from "./linear-mention-slots"

const MENTION_STALE_TIME = 5 * 60 * 1000

export function CommentBody({
	html,
	fromLinear,
}: {
	html: string
	fromLinear: boolean
}) {
	const ref = useRef<HTMLDivElement>(null)
	const sanitized = useMemo(() => sanitizeMessageHtml(html), [html])
	const [slots, setSlots] = useState<LinearMentionSlot[]>([])

	useLayoutEffect(() => {
		if (!ref.current) return
		setSlots(claimLinearMentionSlots(ref.current))
	}, [sanitized])

	const lookup = useMemo(
		() => linearMentionLookup(slots.map((slot) => slot.mention)),
		[slots],
	)
	const { data: resolved } = api.linear.mentions.useQuery(lookup, {
		enabled: hasLinearMentionLookup(lookup),
		staleTime: MENTION_STALE_TIME,
		retry: false,
	})

	return (
		<>
			<div
				ref={ref}
				data-origin={fromLinear ? "linear" : undefined}
				className={cn(
					typographyVariants({ variant: "message" }),
					"comment-body text-sm dark:text-gray-50",
				)}
				dangerouslySetInnerHTML={{ __html: sanitized }}
			/>
			{slots.map((slot, index) =>
				createPortal(
					<LinearMentionBadge mention={slot.mention} resolved={resolved} />,
					slot.container,
					`${index}-${slot.mention.href}`,
				),
			)}
		</>
	)
}
